import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	addSuspiciousWord,
	deleteSuspiciousWord,
	getSuspiciousWords,
	getUserChatConversations,
	getUserChatMessages,
} from "../../api/apiServiceFetch";
import { utcToLocalDateTime } from "../../components/helper";
import ModerationUserCard from "../../components/ModerationUserCard";
import PhotoModal from "../../components/PhotoModal";
import { useAuth } from "../../contexts/auth";
import { API_BASE_URL, getFileBaseUrl } from "../../config/environment";
import userPng from "../Reports/user.png";
import "../SupportMessages/SupportMessages.scss";
import "./UserChats.scss";

const ALLOWED_ADMIN_IDS = [1, 5];

const getListFromResponse = (payload) => {
	if (Array.isArray(payload?.data)) return payload.data;
	if (Array.isArray(payload)) return payload;
	return [];
};

const formatDateTime = (value) => {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);
	return utcToLocalDateTime(value);
};

const getAvatarSrc = (participant) =>
	participant?.mainPhoto
		? `${getFileBaseUrl()}/Photo/${participant.userId}/${participant.mainPhoto}`
		: userPng;

const handleAvatarError = (event) => {
	if (event.currentTarget.src !== userPng) {
		event.currentTarget.src = userPng;
	}
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const renderHighlighted = (text, highlight) => {
	if (!text || !highlight) return text;
	const parts = String(text).split(highlight.regex);
	return parts.map((part, index) =>
		highlight.set.has(part.toLowerCase()) ? (
			<mark key={index} className="user-chats__hl">
				{part}
			</mark>
		) : (
			<React.Fragment key={index}>{part}</React.Fragment>
		),
	);
};

const attachmentLabel = (type) => {
	if (type === "image") return "[фото]";
	if (type === "voice") return "[голосовое]";
	return `[${type}]`;
};

// The signed streaming link the backend mints is rooted at the API host, not at
// the panel's own origin.
const API_ORIGIN = (() => {
	try {
		return new URL(API_BASE_URL).origin;
	} catch {
		return "";
	}
})();

/**
 * Where to load a chat attachment from: the ready link the backend hands out
 * (presigned bucket URL, or a signed path to its streaming endpoint), falling
 * back to the file name for messages sent before media moved to the bucket.
 */
const attachmentSrc = (message) => {
	const url = message?.attachmentUrl?.trim();
	if (url) {
		return url.startsWith("/") ? `${API_ORIGIN}${url}` : url;
	}

	if (message?.attachmentFileName) {
		return `${getFileBaseUrl()}/Chat/${message.senderId}/${encodeURIComponent(
			message.attachmentFileName,
		)}`;
	}

	return null;
};

const formatDuration = (ms) => {
	if (!ms || ms < 0) return "";
	const total = Math.round(ms / 1000);
	return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

const UserChats = () => {
	const { userBase } = useAuth();

	const [conversations, setConversations] = useState([]);
	const [convLoading, setConvLoading] = useState(true);
	const [convError, setConvError] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [appliedSearch, setAppliedSearch] = useState("");
	const [suspiciousOnly, setSuspiciousOnly] = useState(false);

	const [selectedConv, setSelectedConv] = useState(null);
	const [messages, setMessages] = useState([]);
	const [threadLoading, setThreadLoading] = useState(false);
	const [threadError, setThreadError] = useState("");
	const [nextBefore, setNextBefore] = useState(null);
	const [hasMore, setHasMore] = useState(false);
	const [olderLoading, setOlderLoading] = useState(false);
	const threadEndRef = useRef(null);

	const [words, setWords] = useState([]);
	const [wordsOpen, setWordsOpen] = useState(false);
	const [newWord, setNewWord] = useState("");
	const [wordsBusy, setWordsBusy] = useState(false);

	const [cardUser, setCardUser] = useState(null);
	const [cardOpen, setCardOpen] = useState(false);

	const [photoPreview, setPhotoPreview] = useState(null);
	const relinkedRef = useRef(new Set());

	const allowed =
		!!userBase && ALLOWED_ADMIN_IDS.includes(Number(userBase.adminUserId));

	const loadConversations = useCallback(async (term, suspicious) => {
		try {
			setConvLoading(true);
			setConvError("");
			const data = await getUserChatConversations({
				searchTerm: term || undefined,
				suspiciousOnly: suspicious,
				pageSize: 40,
			});
			setConversations(getListFromResponse(data));
		} catch (err) {
			console.error("Failed to load user chats:", err);
			setConversations([]);
			setConvError("Не удалось загрузить чаты пользователей.");
		} finally {
			setConvLoading(false);
		}
	}, []);

	const loadWords = useCallback(async () => {
		try {
			const data = await getSuspiciousWords();
			setWords(getListFromResponse(data));
		} catch (err) {
			console.error("Failed to load suspicious words:", err);
		}
	}, []);

	useEffect(() => {
		if (allowed) {
			loadConversations(appliedSearch, suspiciousOnly);
		}
	}, [allowed, appliedSearch, suspiciousOnly, loadConversations]);

	useEffect(() => {
		if (allowed) {
			loadWords();
		}
	}, [allowed, loadWords]);

	useEffect(() => {
		if (threadEndRef.current) {
			threadEndRef.current.scrollIntoView({ block: "end" });
		}
	}, [selectedConv]);

	const openConversation = useCallback(async (conv) => {
		setSelectedConv(conv);
		setCardOpen(false);
		setThreadLoading(true);
		setThreadError("");
		setMessages([]);
		setNextBefore(null);
		setHasMore(false);
		relinkedRef.current = new Set();
		try {
			const data = await getUserChatMessages(conv.conversationId, {
				pageSize: 60,
			});
			const items = Array.isArray(data?.messages)
				? data.messages.slice().reverse()
				: [];
			setMessages(items);
			setNextBefore(data?.nextBeforeMessageId ?? null);
			setHasMore(!!data?.hasMore);
		} catch (err) {
			console.error("Failed to load chat thread:", err);
			setThreadError("Не удалось загрузить переписку.");
		} finally {
			setThreadLoading(false);
		}
	}, []);

	const loadOlder = useCallback(async () => {
		if (!selectedConv || !hasMore || !nextBefore || olderLoading) return;
		try {
			setOlderLoading(true);
			const data = await getUserChatMessages(selectedConv.conversationId, {
				beforeMessageId: nextBefore,
				pageSize: 60,
			});
			const older = Array.isArray(data?.messages)
				? data.messages.slice().reverse()
				: [];
			setMessages((current) => [...older, ...current]);
			setNextBefore(data?.nextBeforeMessageId ?? null);
			setHasMore(!!data?.hasMore);
		} catch (err) {
			console.error("Failed to load older messages:", err);
		} finally {
			setOlderLoading(false);
		}
	}, [selectedConv, hasMore, nextBefore, olderLoading]);

	// Attachment links are short-lived, so one left open long enough stops
	// loading. Re-reading just that message mints a fresh link; once per
	// message, otherwise a genuinely missing file loops forever.
	const relinkAttachment = useCallback(
		async (message) => {
			if (!selectedConv || relinkedRef.current.has(message.id)) return;
			relinkedRef.current.add(message.id);

			try {
				const data = await getUserChatMessages(selectedConv.conversationId, {
					beforeMessageId: message.id + 1,
					pageSize: 1,
				});
				const fresh = Array.isArray(data?.messages) ? data.messages[0] : null;
				if (fresh?.id !== message.id || !fresh.attachmentUrl) return;

				setMessages((current) =>
					current.map((item) =>
						item.id === fresh.id
							? { ...item, attachmentUrl: fresh.attachmentUrl }
							: item,
					),
				);
			} catch (err) {
				console.error("Failed to refresh attachment link:", err);
			}
		},
		[selectedConv],
	);

	const handleBackToList = useCallback(() => {
		setSelectedConv(null);
		setMessages([]);
		setThreadError("");
	}, []);

	const handleSearchSubmit = useCallback(
		(event) => {
			event.preventDefault();
			setAppliedSearch(searchTerm.trim());
		},
		[searchTerm],
	);

	const openCard = useCallback((participant) => {
		if (!participant) return;
		setCardUser(participant);
		setCardOpen(true);
	}, []);

	const handleAddWord = useCallback(async () => {
		const word = newWord.trim();
		if (!word || wordsBusy) return;
		try {
			setWordsBusy(true);
			await addSuspiciousWord(word);
			setNewWord("");
			await loadWords();
			loadConversations(appliedSearch, suspiciousOnly);
		} catch (err) {
			console.error("Failed to add suspicious word:", err);
		} finally {
			setWordsBusy(false);
		}
	}, [newWord, wordsBusy, loadWords, loadConversations, appliedSearch, suspiciousOnly]);

	const handleDeleteWord = useCallback(
		async (id) => {
			if (wordsBusy) return;
			try {
				setWordsBusy(true);
				await deleteSuspiciousWord(id);
				await loadWords();
				loadConversations(appliedSearch, suspiciousOnly);
			} catch (err) {
				console.error("Failed to delete suspicious word:", err);
			} finally {
				setWordsBusy(false);
			}
		},
		[wordsBusy, loadWords, loadConversations, appliedSearch, suspiciousOnly],
	);

	const highlight = useMemo(() => {
		const terms = [...words.map((w) => w.word), appliedSearch]
			.map((value) => (value || "").trim())
			.filter(Boolean);
		if (terms.length === 0) return null;
		const escaped = terms.map(escapeRegExp).filter(Boolean);
		if (escaped.length === 0) return null;
		return {
			regex: new RegExp(`(${escaped.join("|")})`, "gi"),
			set: new Set(terms.map((value) => value.toLowerCase())),
		};
	}, [words, appliedSearch]);

	if (userBase && !allowed) {
		return (
			<div className="user-chats">
				<h2>Чаты пользователей</h2>
				<div className="support-messages__status support-messages__status--error">
					Доступ ограничен. Раздел доступен только выбранным администраторам.
				</div>
			</div>
		);
	}

	if (!userBase) {
		return (
			<div className="user-chats">
				<h2>Чаты пользователей</h2>
				<div className="support-messages__status">Загрузка...</div>
			</div>
		);
	}

	return (
		<div className="user-chats support-messages">
			<div className="user-chats__head">
				<h2>Чаты пользователей</h2>
				<button
					type="button"
					className="user-chats__words-btn"
					onClick={() => setWordsOpen(true)}
				>
					Подозрительные слова ({words.length})
				</button>
			</div>

			<ModerationUserCard
				user={cardUser}
				isOpen={cardOpen}
				onClose={() => setCardOpen(false)}
				onUserUpdated={() => loadConversations(appliedSearch, suspiciousOnly)}
			/>

			{photoPreview && (
				<PhotoModal
					photo={photoPreview}
					onClose={() => setPhotoPreview(null)}
				/>
			)}

			{wordsOpen && (
				<div
					className="user-chats__modal-overlay"
					onClick={() => setWordsOpen(false)}
				>
					<div
						className="user-chats__modal"
						onClick={(event) => event.stopPropagation()}
					>
						<div className="user-chats__modal-head">
							<h3>Подозрительные слова</h3>
							<button type="button" onClick={() => setWordsOpen(false)}>
								✕
							</button>
						</div>
						<div className="user-chats__word-add">
							<input
								type="text"
								placeholder="Новое слово"
								value={newWord}
								onChange={(event) => setNewWord(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === "Enter") handleAddWord();
								}}
							/>
							<button type="button" disabled={wordsBusy} onClick={handleAddWord}>
								Добавить
							</button>
						</div>
						<div className="user-chats__word-list">
							{words.length === 0 && (
								<div className="support-messages__status">Список пуст</div>
							)}
							{words.map((word) => (
								<div key={word.id} className="user-chats__word">
									<span>{word.word}</span>
									<button
										type="button"
										disabled={wordsBusy}
										onClick={() => handleDeleteWord(word.id)}
										title="Удалить"
									>
										✕
									</button>
								</div>
							))}
						</div>
					</div>
				</div>
			)}

			<div
				className={`support-chat${
					selectedConv ? " support-chat--thread-open" : ""
				}`}
			>
				<aside className="support-chat__sidebar">
					<form className="support-chat__search" onSubmit={handleSearchSubmit}>
						<input
							type="text"
							placeholder="Поиск: id, tg, почта, имя, никнейм, текст"
							value={searchTerm}
							onChange={(event) => setSearchTerm(event.target.value)}
						/>
						<button type="submit">Найти</button>
					</form>

					<label className="user-chats__toggle">
						<input
							type="checkbox"
							checked={suspiciousOnly}
							onChange={(event) => setSuspiciousOnly(event.target.checked)}
						/>
						Только подозрительные
					</label>

					{convLoading && (
						<div className="support-messages__status">Загрузка...</div>
					)}
					{convError && !convLoading && (
						<div className="support-messages__status support-messages__status--error">
							{convError}
						</div>
					)}
					{!convLoading && !convError && conversations.length === 0 && (
						<div className="support-messages__status">Нет чатов</div>
					)}

					<div className="support-chat__dialogs">
						{conversations.map((conv) => (
							<button
								key={conv.conversationId}
								type="button"
								className={`support-chat__dialog${
									selectedConv?.conversationId === conv.conversationId
										? " support-chat__dialog--active"
										: ""
								}`}
								onClick={() => openConversation(conv)}
							>
								<span className="user-chats__pair-avatars">
									<img
										className="support-chat__avatar"
										src={getAvatarSrc(conv.userOne)}
										onError={handleAvatarError}
										onClick={(event) => {
											event.stopPropagation();
											openCard(conv.userOne);
										}}
										alt=""
									/>
									<img
										className="support-chat__avatar user-chats__avatar--second"
										src={getAvatarSrc(conv.userTwo)}
										onError={handleAvatarError}
										onClick={(event) => {
											event.stopPropagation();
											openCard(conv.userTwo);
										}}
										alt=""
									/>
								</span>
								<div className="support-chat__dialog-body">
									<div className="support-chat__dialog-top">
										<span className="support-chat__dialog-name">
											{conv.userOne?.display} — {conv.userTwo?.display}
										</span>
										<span className="support-chat__dialog-time">
											{formatDateTime(conv.lastMessageAt)}
										</span>
									</div>
									<div className="support-chat__dialog-bottom">
										<span className="support-chat__dialog-preview">
											{conv.lastMessageType && conv.lastMessageType !== "text"
												? attachmentLabel(conv.lastMessageType)
												: conv.lastMessageText || <em>Нет сообщений</em>}
										</span>
										{conv.hasSuspicious && (
											<span className="user-chats__badge" title="Найдены подозрительные слова">
												!
											</span>
										)}
									</div>
								</div>
							</button>
						))}
					</div>
				</aside>

				<section className="support-chat__thread">
					{!selectedConv && (
						<div className="support-chat__placeholder">
							Выберите чат слева, чтобы открыть переписку.
						</div>
					)}

					{selectedConv && (
						<>
							<div className="support-chat__thread-header">
								<button
									type="button"
									className="support-chat__thread-back"
									onClick={handleBackToList}
									title="К списку чатов"
									aria-label="К списку чатов"
								>
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M10 13L5 8L10 3"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</button>
								<button
									type="button"
									className="support-chat__thread-user"
									onClick={() => openCard(selectedConv.userOne)}
									title="Открыть карточку пользователя"
								>
									<img
										className="support-chat__avatar support-chat__avatar--header"
										src={getAvatarSrc(selectedConv.userOne)}
										onError={handleAvatarError}
										alt=""
									/>
									<span className="support-chat__thread-name">
										{selectedConv.userOne?.display}
									</span>
								</button>
								<span className="user-chats__thread-sep">↔</span>
								<button
									type="button"
									className="support-chat__thread-user"
									onClick={() => openCard(selectedConv.userTwo)}
									title="Открыть карточку пользователя"
								>
									<img
										className="support-chat__avatar support-chat__avatar--header"
										src={getAvatarSrc(selectedConv.userTwo)}
										onError={handleAvatarError}
										alt=""
									/>
									<span className="support-chat__thread-name">
										{selectedConv.userTwo?.display}
									</span>
								</button>
							</div>

							<div className="support-chat__messages">
								{threadLoading && (
									<div className="support-messages__status">Загрузка...</div>
								)}
								{threadError && !threadLoading && (
									<div className="support-messages__status support-messages__status--error">
										{threadError}
									</div>
								)}
								{!threadLoading && !threadError && messages.length === 0 && (
									<div className="support-messages__status">
										В этом чате пока нет сообщений
									</div>
								)}

								{!threadLoading && !threadError && hasMore && (
									<button
										type="button"
										className="user-chats__load-older"
										disabled={olderLoading}
										onClick={loadOlder}
									>
										{olderLoading ? "Загрузка..." : "Показать более ранние"}
									</button>
								)}

								{!threadLoading &&
									!threadError &&
									messages.map((message) => {
										const isRight =
											message.senderId === selectedConv.userTwo?.userId;
										const sender = isRight
											? selectedConv.userTwo
											: selectedConv.userOne;
										const isAttachment =
											message.type && message.type !== "text";
										const src = isAttachment ? attachmentSrc(message) : null;
										return (
											<div
												key={message.id}
												className={`support-chat__message${
													isRight ? " support-chat__message--admin" : ""
												}`}
											>
												<div className="support-chat__bubble">
													<div className="user-chats__msg-sender">
														{sender?.display || `User #${message.senderId}`}
													</div>
													<div className="support-chat__bubble-text">
														{!isAttachment &&
															renderHighlighted(message.body, highlight)}

														{isAttachment && !src && (
															<em>{attachmentLabel(message.type)}</em>
														)}

														{isAttachment && src && message.type === "image" && (
															<button
																type="button"
																className="user-chats__msg-photo"
																onClick={() => setPhotoPreview(src)}
																title="Открыть фото"
															>
																<img
																	src={src}
																	alt="Фото из чата"
																	loading="lazy"
																	onError={() => relinkAttachment(message)}
																/>
															</button>
														)}

														{isAttachment && src && message.type === "voice" && (
															<div className="user-chats__msg-voice">
																{/* eslint-disable-next-line jsx-a11y/media-has-caption */}
																<audio
																	controls
																	preload="none"
																	src={src}
																	onError={() => relinkAttachment(message)}
																/>
																{message.attachmentDurationMs > 0 && (
																	<span className="user-chats__msg-voice-time">
																		{formatDuration(message.attachmentDurationMs)}
																	</span>
																)}
															</div>
														)}

														{isAttachment &&
															src &&
															message.type !== "image" &&
															message.type !== "voice" && (
																<a
																	href={src}
																	target="_blank"
																	rel="noreferrer"
																>
																	{attachmentLabel(message.type)}
																</a>
															)}
													</div>
													<div className="support-chat__bubble-time">
														{formatDateTime(message.sentAt)}
													</div>
												</div>
											</div>
										);
									})}
								<div ref={threadEndRef} />
							</div>
						</>
					)}
				</section>
			</div>
		</div>
	);
};

export default UserChats;
